import React, { useMemo, useState } from "react";
import VStack from "../../ui_atoms/VStack";
import { StyleSheet, TextInput as RNTextInput } from "react-native";
import { ActivityType, LaaActivityType, Mission } from "../../__generated__/graphql";
import { useI18n } from "../../hooks/i18n-hooks";
import TextField from "../TextField";
import { useTheme } from "../../hooks/use-theme";
import { Buildable } from "ts-essentials";
import SelectField from "../SelectField";
import { formatActivityTypeTitle } from "../../utils";
import { useLazyQuery, useQuery } from "@apollo/client";
import { FIND_ACTIVITY_TYPE, GET_PRIVATE_CLIENT_ACTIVITY_TYPES } from "../../graphql/core";
import Callout from "../../ui_atoms/Callout";
import OptionsSelector from "../OptionsSelector";
import { BodyMediumText, BodySmallText, LinkSmallTextBold } from "../FormattedText";
import { useCurrentProfile } from "../../hooks/use-current-profile";
import { useIntercom } from "../../hooks/intercom";

type MissionJobTitleProps = {
    mission: Buildable<Mission>;
    setMission: (mission: Buildable<Mission>) => void;
};

const MissionJobTitle: React.FC<MissionJobTitleProps> = ({ mission, setMission }) => {
    const i18n = useI18n();
    const styles = useStyles();
    const { colors } = useTheme();
    const { presentArticleId } = useIntercom();
    const descriptionRef = React.useRef<RNTextInput>(null);
    const [jobTitle, setJobTitle] = useState("");
    const currentProfile = useCurrentProfile();
    const isPrivateClient = currentProfile?.isPrivateClient || false;

    const workerGender = mission.workerInfo?.gender;
    const [getActivityType] = useLazyQuery(FIND_ACTIVITY_TYPE);
    const privateClientActivityTypes = useQuery(GET_PRIVATE_CLIENT_ACTIVITY_TYPES, {
        skip: !isPrivateClient,
        variables: { stateCode: mission.currentContract?.workingAddress?.locality?.state?.code },
    });

    const onFilterChange = React.useCallback(
        async (query: string) => {
            if (query.length > 2) {
                const result = await getActivityType({
                    variables: { query, limit: 50 },
                });
                if (!result.data) {
                    return [];
                }
                if (!workerGender) {
                    throw new Error("Mission's worker info's gender is not defined");
                }
                return result.data.findActivityType.elements
                    .map((at) => ({
                        value: at.id,
                        label: formatActivityTypeTitle(at.title, workerGender),
                    }))
                    .sort((a, b) => a.label.length - b.label.length) as {
                    value: string;
                    label: string;
                }[];
            } else {
                return null;
            }
        },
        [getActivityType, workerGender],
    );

    const jobTitlesData = useMemo(
        () =>
            !isPrivateClient
                ? onFilterChange
                : (privateClientActivityTypes.data?.getPrivateClientActivityTypes || []).map(
                      (at) => ({
                          value: at,
                          label: at.name,
                      }),
                  ),
        [
            isPrivateClient,
            onFilterChange,
            privateClientActivityTypes.data?.getPrivateClientActivityTypes,
        ],
    );

    const setActivityType = React.useCallback(
        (value: Buildable<ActivityType>) => {
            setMission({
                ...mission,
                activityType: value,
                currentContract: {
                    ...mission.currentContract,
                    jobTitle: value.title,
                },
            });
        },
        [mission, setMission],
    );

    const setMissionJobTitle = React.useCallback(
        (value: string) => {
            setMission({
                ...mission,
                currentContract: {
                    ...mission.currentContract,
                    jobTitle: value || mission.activityType?.title,
                },
            });
        },
        [mission, setMission],
    );

    const setJobDescription = React.useCallback(
        (value: string) => {
            setMission({
                ...mission,
                currentContract: {
                    ...mission.currentContract,
                    jobDescription: value,
                },
            });
        },
        [mission, setMission],
    );
    const setOfficeJob = React.useCallback(
        (value: string) => {
            setMission({
                ...mission,
                laaActivityType:
                    value === "yes" ? LaaActivityType.Office : LaaActivityType.Technical,
            });
        },
        [mission, setMission],
    );

    const setPrivateJob = React.useCallback(
        (
            activityType: Buildable<ActivityType>,
            laaActivityType: LaaActivityType,
            cctEditionId: string,
        ) => {
            setMission({
                ...mission,
                activityType: activityType,
                currentContract: {
                    ...mission.currentContract,
                    jobTitle: activityType.title,
                    cctEditionId,
                },
                laaActivityType,
            });
        },
        [mission, setMission],
    );

    return (
        <VStack gap={1}>
            {!isPrivateClient ? (
                <BodyMediumText>
                    {i18n.t("screens.user.create_mission_job_title.office_job")}
                </BodyMediumText>
            ) : (
                <BodyMediumText color={colors.greyAccessible}>
                    {i18n.t("screens.user.create_mission_job_title.description_top")}
                </BodyMediumText>
            )}

            {!isPrivateClient && (
                <OptionsSelector
                    data={[
                        {
                            value: "yes",
                            label: i18n.t("screens.user.create_mission_job_title.yes"),
                        },
                        { value: "no", label: i18n.t("screens.user.create_mission_job_title.no") },
                    ]}
                    value={
                        mission.laaActivityType
                            ? mission.laaActivityType === LaaActivityType.Office
                                ? "yes"
                                : "no"
                            : ""
                    }
                    onSelect={setOfficeJob}
                />
            )}
            <>
                <BodySmallText color={colors.greyAccessible}>
                    {i18n.t("screens.user.create_mission_job_title.description_allowances")}
                </BodySmallText>
                <LinkSmallTextBold
                    onPress={() => {
                        void presentArticleId("10977514");
                    }}
                >
                    {i18n.t("know_more")}
                </LinkSmallTextBold>
            </>
            <SelectField<
                | string
                | {
                      key: string;
                      name: string;
                      laaActivityType: LaaActivityType;
                      activityTypeId: string;
                      cctEditionId: string;
                  }
            >
                style={styles.items}
                isDynamicHeight
                label={i18n.t("screens.user.create_mission_job_title.job")}
                value={
                    mission.activityType?.title && mission.workerInfo?.gender
                        ? !isPrivateClient
                            ? formatActivityTypeTitle(mission.activityType.title, workerGender)
                            : mission.activityType.title
                        : undefined
                }
                data={jobTitlesData}
                searchPlaceholder={i18n.t("screens.user.create_mission_activity_type.search")}
                onSelect={(selectedItem) => {
                    if (typeof selectedItem.value === "string") {
                        setActivityType({
                            id: selectedItem.value,
                            title: selectedItem.label,
                        });
                    } else if (isPrivateClient) {
                        setPrivateJob(
                            {
                                id: selectedItem.value.activityTypeId,
                                title: selectedItem.label,
                            },
                            selectedItem.value.laaActivityType,
                            selectedItem.value.cctEditionId,
                        );
                    }
                }}
                emptyText={i18n.t("type_to_search")}
                emptyResult={
                    <Callout
                        mode="warning"
                        markdown={i18n.t("screens.user.create_mission_activity_type.no_result")}
                    />
                }
            />
            {!isPrivateClient && (
                <TextField
                    style={styles.items}
                    label={i18n.t("screens.user.create_mission_job_title.job_title")}
                    value={jobTitle}
                    returnKeyType="default"
                    onChangeText={(value) => {
                        setJobTitle(value);
                        setMissionJobTitle(value);
                    }}
                    onSubmitEditing={() => {
                        setJobTitle(mission.currentContract?.jobTitle || "");
                        descriptionRef.current?.focus();
                    }}
                />
            )}
            <TextField
                ref={descriptionRef}
                label={i18n.t("screens.user.create_mission_job_title.description")}
                value={mission.currentContract?.jobDescription || ""}
                onChangeText={setJobDescription}
                multiline
            />
        </VStack>
    );
};

const useStyles = () => {
    const { fonts, colors, gridSize } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                items: {
                    textAlign: "auto",
                    width: "auto",
                    alignSelf: "stretch",
                },
                cctView: {
                    marginTop: gridSize / 2,
                },
                cctTitle: {
                    ...fonts.regular,
                },
                cctDescription: {
                    ...fonts.bodySmall,
                    color: colors.fieldLabel,
                },
                cctDetailsItem: {
                    marginHorizontal: -(gridSize / 1.5),
                },
            }),
        [colors.fieldLabel, fonts.bodySmall, fonts.regular, gridSize],
    );
};

export default MissionJobTitle;
